import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Download, ExternalLink, Film, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_LABELS = {
  video_agent:   'Video Agent',
  avatar_video:  'Avatar Video',
  translate:     'Translated Video',
  photo_avatar:  'Photo Avatar',
};

const SharedVideoPage = () => {
  const { token } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/heygen/shared/${token}`);
        setVideo(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Video not found or link expired');
      }
      setLoading(false);
    };
    load();
  }, [token]);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = video.video_url;
    a.download = `${video.title || 'video'}.mp4`;
    a.target = '_blank';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-xl">
        {/* Brand */}
        <div className="text-center mb-6">
          <p className="text-gray-400 text-xs mb-1">AI-generated video shared via</p>
          <p className="text-white font-bold text-xl">Nerd AI · Video Studio</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Loading video...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-3">
              <AlertCircle className="h-10 w-10 text-red-400" />
              <p className="font-semibold text-gray-800">Video Not Available</p>
              <p className="text-sm text-gray-500">{error}</p>
              <p className="text-xs text-gray-400">Links expire after 30 days</p>
            </div>
          ) : (
            <>
              {/* Video player */}
              <div className="bg-black">
                <video
                  src={video.video_url}
                  controls
                  autoPlay
                  muted
                  playsInline
                  className="w-full max-h-[420px]"
                  data-testid="shared-video-player"
                />
              </div>

              {/* Info */}
              <div className="p-4 sm:p-6 space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Film className="h-4 w-4 text-gray-400" />
                    <span className="text-xs text-gray-400">{TYPE_LABELS[video.type] || video.type}</span>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{video.title || 'AI Generated Video'}</h2>
                  {video.created_at && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Created {new Date(video.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleDownload}
                    className="flex-1 bg-gray-900 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 text-sm hover:bg-gray-700"
                    data-testid="shared-download-btn"
                  >
                    <Download className="h-4 w-4" /> Download Video
                  </button>
                  <a
                    href={video.video_url} target="_blank" rel="noreferrer"
                    className="flex-1 border-2 border-gray-300 text-gray-700 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50 text-sm"
                  >
                    <ExternalLink className="h-4 w-4" /> Open Full Screen
                  </a>
                </div>

                <div className="border-t pt-3 text-center">
                  <p className="text-xs text-gray-400">
                    Want to create AI videos like this?{' '}
                    <a href="/" className="text-gray-700 font-semibold underline">Try Nerd AI for free</a>
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedVideoPage;
